import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPLIANCE_LABEL, complianceClasses, categoryClasses, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, MapPin, Scale, Landmark, Compass, Shield, FileSpreadsheet } from "lucide-react";
import { LocationFields, PoliceFields } from "@/components/LocationFields";

const CATEGORIES = ["Agricultural", "Commercial", "Residential"];
const DIRECTIONS = ["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"];
const SHAPES = ["Square", "Rectangle", "Irregular", "Triangular", "L-shaped"];
const RATINGS = ["Excellent", "Good", "Average", "Needs correction", "Not assessed"];

const EMPTY = {
  name: "", survey_number: "", village: "", mandal: "", district: "", state: "Telangana",
  extent_acres: "", asset_category: "Agricultural", land_type: "", owner_name: "",
  khata_number: "", latitude: "", longitude: "", boundaries: "", acquisition_date: "",
  acquisition_value: "", status: "Active", notes: "", assigned_executive_id: "",
  visit_frequency: "monthly", has_litigation: false, has_mortgage: false,
  pincode: "", police_station: "", police_station_phone: "", police_station_address: "",
  police_jurisdiction_notes: "",
  vaastu_facing: "", vaastu_shape: "", vaastu_slope: "", vaastu_water_source: "",
  vaastu_road_position: "", vaastu_rating: "Not assessed", vaastu_notes: "",
};

export default function Parcels() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [parcels, setParcels] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (category !== "all") params.asset_category = category;
    api.get("/parcels", { params }).then(({ data }) => setParcels(data)).catch(() => {});
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category]);

  useEffect(() => {
    if (isAdmin) api.get("/users").then(({ data }) => setExecutives(data.filter((u) => u.role === "executive"))).catch(() => {});
  }, [isAdmin]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/parcels", {
        ...form,
        extent_acres: parseFloat(form.extent_acres) || 0,
        latitude: form.latitude === "" ? null : parseFloat(form.latitude),
        longitude: form.longitude === "" ? null : parseFloat(form.longitude),
        acquisition_value: form.acquisition_value === "" ? null : parseFloat(form.acquisition_value),
        acquisition_date: form.acquisition_date || null,
        assigned_executive_id: form.assigned_executive_id || null,
      });
      toast.success("Asset onboarded");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label className="label-tiny">{label}</Label>
      <Input className="mt-2" data-testid={`parcel-${key}-input`} value={form[key]}
        onChange={(e) => set(key)(e.target.value)} {...props} />
    </div>
  );

  const picker = (label, key, options, testid) => (
    <div>
      <Label className="label-tiny">{label}</Label>
      <Select value={form[key] || "none"} onValueChange={(v) => set(key)(v === "none" ? "" : v)}>
        <SelectTrigger className="mt-2" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Not specified</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-5 md:p-10 max-w-[1500px] rise" data-testid="parcels-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-tiny">Portfolio</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">Asset register</h1>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-3">
            <a href={`${API}/export/registers.xlsx?which=all&auth=${localStorage.getItem("token")}`}
              data-testid="export-registers-link"
              className="inline-flex items-center gap-2 text-sm font-medium rounded-lg border border-border px-4 py-2.5 bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-200">
              <FileSpreadsheet size={15} /> Export registers
            </a>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-parcel-button" className="rounded-lg active:scale-[0.98] transition-transform duration-150">
                <Plus size={16} className="mr-2" /> Onboard asset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display font-bold tracking-tight">Onboard new asset</DialogTitle>
                <DialogDescription>
                  Capture title, location, vaastu orientation and flags. Documents, litigation and
                  mortgage details are added on the asset page.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-6" data-testid="parcel-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">{field("Asset name *", "name", { required: true, placeholder: "Shamirpet Block A" })}</div>
                  <div>
                    <Label className="label-tiny">Category</Label>
                    <Select value={form.asset_category} onValueChange={set("asset_category")}>
                      <SelectTrigger className="mt-2" data-testid="parcel-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {field("Sub type", "land_type", { placeholder: "Dry / Wet / Shop / Plot" })}
                  {field("Survey number *", "survey_number", { required: true, placeholder: "123/4A" })}
                  {field("Khata / Passbook no.", "khata_number")}
                  {field("Village *", "village", { required: true })}
                  {field("Mandal / Taluk", "mandal")}
                  <LocationFields state={form} setState={setForm} prefix="parcel" />
                  {field("Extent (acres)", "extent_acres", { type: "number", step: "0.01" })}
                  {field("Owner / Title holder", "owner_name")}
                  {field("Latitude", "latitude", { type: "number", step: "any" })}
                  {field("Longitude", "longitude", { type: "number", step: "any" })}
                  <div>
                    <Label className="label-tiny">Assigned executive</Label>
                    <Select value={form.assigned_executive_id || "none"}
                      onValueChange={(v) => set("assigned_executive_id")(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-2" data-testid="parcel-executive-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {executives.map((ex) => (
                          <SelectItem key={ex.id} value={ex.id} data-testid={`executive-option-${ex.id}`}>{ex.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tiny">Visit frequency</Label>
                    <Select value={form.visit_frequency} onValueChange={set("visit_frequency")}>
                      <SelectTrigger className="mt-2" data-testid="parcel-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded-xl border border-border p-4 space-y-4">
                  <p className="label-tiny flex items-center gap-2"><Shield size={14} className="text-primary" /> Police station jurisdiction</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <PoliceFields state={form} setState={setForm} prefix="parcel" />
                  </div>
                </div>

                <div className="rounded-xl border border-border p-4 space-y-4">
                  <p className="label-tiny flex items-center gap-2"><Compass size={14} className="text-primary" /> Vaastu &amp; orientation (optional)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {picker("Facing direction", "vaastu_facing", DIRECTIONS, "parcel-facing-select")}
                    {picker("Plot shape", "vaastu_shape", SHAPES, "parcel-shape-select")}
                    {picker("Road position", "vaastu_road_position", DIRECTIONS, "parcel-road-select")}
                    {picker("Slope towards", "vaastu_slope", DIRECTIONS, "parcel-slope-select")}
                    {picker("Water source at", "vaastu_water_source", DIRECTIONS, "parcel-water-select")}
                    {picker("Vaastu rating", "vaastu_rating", RATINGS, "parcel-vaastu-rating-select")}
                  </div>
                  <div>
                    <Label className="label-tiny">Vaastu notes</Label>
                    <Textarea rows={3} className="mt-2" data-testid="parcel-vaastu-notes-input"
                      value={form.vaastu_notes} onChange={(e) => set("vaastu_notes")(e.target.value)}
                      placeholder="North-east corner open, no cuts. Compass screenshot to be attached after onboarding." />
                    <p className="mt-2 text-xs text-muted-foreground">
                      Upload compass screenshots from the asset page → Vaastu tab.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Scale size={16} className="text-red-500" /> Court case / litigation
                    </span>
                    <Switch checked={form.has_litigation} onCheckedChange={set("has_litigation")}
                      data-testid="parcel-litigation-switch" />
                  </label>
                  <label className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Landmark size={16} className="text-cyan-500" /> Mortgage / loan
                    </span>
                    <Switch checked={form.has_mortgage} onCheckedChange={set("has_mortgage")}
                      data-testid="parcel-mortgage-switch" />
                  </label>
                </div>

                <div>
                  <Label className="label-tiny">Boundaries / Notes</Label>
                  <Textarea className="mt-2" rows={3} data-testid="parcel-notes-input"
                    value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={busy} data-testid="parcel-submit-button" className="rounded-lg">
                    {busy ? "Saving…" : "Onboard asset"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="parcel-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, survey number or village" className="pl-9 h-11" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-52 h-11" data-testid="parcel-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {parcels.length === 0 ? (
        <div className="mt-10 surface p-12 text-center" data-testid="parcels-empty">
          <p className="font-display text-xl font-bold tracking-tight">No assets found</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {isAdmin ? "Onboard your first property to start monitoring." : "No assets are assigned to you yet."}
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="parcels-grid">
          {parcels.map((p) => (
            <Link key={p.id} to={`/parcels/${p.id}`} data-testid={`parcel-card-${p.id}`}
              className="surface p-5 flex flex-col hover:-translate-y-1 hover:shadow-md hover:border-primary/40 transition-[transform,box-shadow,border-color] duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className={`inline-block rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${categoryClasses(p.asset_category)}`}>
                    {p.asset_category || "Agricultural"}
                  </span>
                  <h3 className="mt-2 font-display text-lg font-bold tracking-tight truncate">{p.name}</h3>
                </div>
                <span className={`shrink-0 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${complianceClasses(p.compliance)}`}>
                  {COMPLIANCE_LABEL[p.compliance]}
                </span>
              </div>
              <p className="mt-2.5 text-xs text-muted-foreground flex items-center gap-1.5">
                <MapPin size={13} /> {[p.village, p.mandal, p.district].filter(Boolean).join(", ") || "—"}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3 border-t border-border pt-4 text-xs">
                <div>
                  <p className="label-tiny">Survey</p>
                  <p className="mt-1 font-mono">{p.survey_number || "—"}</p>
                </div>
                <div>
                  <p className="label-tiny">Extent</p>
                  <p className="mt-1 font-mono">{p.extent_acres || 0} ac</p>
                </div>
                <div>
                  <p className="label-tiny">Docs</p>
                  <p className="mt-1 font-mono">{p.document_count}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">{p.assigned_executive?.name || "Unassigned"}</span>
                <span className="flex items-center gap-2 shrink-0">
                  {p.open_case_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-red-600" data-testid={`card-cases-${p.id}`}>
                      <Scale size={12} /> {p.open_case_count}
                    </span>
                  )}
                  {p.active_mortgage_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-cyan-600" data-testid={`card-mortgages-${p.id}`}>
                      <Landmark size={12} /> {p.active_mortgage_count}
                    </span>
                  )}
                  <span className="font-mono">{fmtDate(p.last_visit_at)}</span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
